import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BwjPageContent } from '../../../../framework/components/bwj-page-content';
import { BwjPageHeader } from '../../../../framework/components/bwj-page-header';
import { useParams } from 'react-router-dom';
import { useToast } from '../../../../framework/components/bwj-toast/context';
import * as formService from '../../form-services/door-editor';
import { BwjViewManager, BwjViewState } from '../../../../framework/components/bwj-view-manager';
import { IStormDoorEditingModel, StormDoorEditingModel } from '../../../../entities-generated/models/storm-door/storm-door-editing-model';
import { StormDoorApiClient } from '../../../../services/storm-door';
import { MessageBarType } from '@fluentui/react';
import { IProductTexture } from '../../../../entities-generated/common/product-texture';
import { AppRoutes } from '../../../app-routes';
import { IDoorSizeOption } from '../../../../entities-generated/common/door-size-option';
import { IStormDoorHandleTypeOption } from '../../../../entities-generated/common/storm-door-handle-type-option';
import { BwjCheckbox } from '../../../../framework/components/bwj-checkbox';
import { BwjImageBrowser } from '../../../../framework/components/bwj-image-browser';
import { BwjGrid, Row, Col } from '../../../../framework/components/bwj-layout';
import { Spacing } from '../../../../framework/components/bwj-layout/styles';
import { BwjSingleSelectField } from '../../../../framework/components/bwj-select-field/single';
import { BwjTextField } from '../../../../framework/components/bwj-text-field';
import { TextureVariantListItem } from '../../components/variant-listing';
import { ProductVariantEditorPanel } from '../../components/variant-editor';
import { ProductTextureEditingModel } from '../../../entities/product-texture-editing-model';
import { replaceFirstWhere } from '../../../../framework/utils/collection';

const subscriberId = 'Page.EditStormDoor';

export const PageEditStormDoor: React.FunctionComponent = () => {
  const { id: doorId } = useParams<{id: string}>();
  const { showToast } = useToast();
  const formModel = formService.useFormModel(subscriberId);
  const hasUnsavedChanges = formService.useHasUnsavedChanges(formModel, true);
  const [viewState, setViewState] = useState(BwjViewState.Loading);
  const [door, setDoor] = useState<IStormDoorEditingModel>(new StormDoorEditingModel());
  const [primaryImageDisplayIndex, setPrimaryImageDisplayIndex] = useState(0);
  const [variantEditorArgs, setVariantEditorArgs] = useState<ProductTextureEditingModel | undefined>(undefined);
  const productBaseUrl = useMemo(() => `${door.productUrl.replace(/\/[a-z0-9]+$/, '')}`, [door]);
  const primaryImages = useMemo(() => {
    if(!!formModel) { // always true, just here for linter
      return formService.imageUrls.getOutputValue();
    }
    return []; // will never happen
  }, [formModel]);

  const initializeView = useCallback(() => {
    StormDoorApiClient.getStormDoorForEditing(doorId)
    .then(d => {
      setDoor(d);
      formService.initializeService(d);
      setViewState(BwjViewState.Ready);
    })
    .catch((e) => {
      console.log(e);
      setViewState(BwjViewState.Error);
    });
  }, [doorId]);
  
  useEffect(() => {
    initializeView();
  }, [initializeView]);

  const onUpdateStormDoor = () => {
    formService.validateAndUpdateModel()
    .then(result => {
      if(result.isValid === false) { return null; }
      
      setViewState(BwjViewState.Loading);
      const request = formService.toUpdateRequest();
      return StormDoorApiClient.updateStormDoor(request);
    })
    .then(resp => {
      if(!resp) { return; }
      showToast(MessageBarType.success, `Storm door '${formModel.description.value}' successfully updated`);
      return initializeView();
    })
    .catch(() => {
      showToast(MessageBarType.error, 'An unexpected error occurred');
      setViewState(BwjViewState.Ready);
    });
  }

  const onViewOnVendorSite = () => {
    window.open(`${door.productUrl}`, '_blank');
  }

  const onVariantSelected = (variant: IProductTexture) => {
    const args = new ProductTextureEditingModel(variant, door.doorColors);
    setVariantEditorArgs(args);
  }

  const onUpdateVariant = (variant: IProductTexture) => {
    const variants = replaceFirstWhere(formModel.textureVariants, v => v.id === variant.id, variant);
    formService.textureVariants.onUpdate(variants);
  }

  const onViewVariantOnVendorSite = (variant: IProductTexture) => {
    window.open(`${productBaseUrl}/${variant.id}`, '_blank');
  }

  return (
    <>
    <ProductVariantEditorPanel
      invokePanelDisplay={variantEditorArgs}
      onUpdateVariant={onUpdateVariant}
    />
    <BwjViewManager
      state={viewState}
      loadingState={{
        message: 'Loading storm door...'
      }}
      errorState={{
        message: 'An unexpected error occurred'
      }}
    >
      <BwjPageHeader
        title={door.description}
        titleIconName={AppRoutes.editStormDoor.icon}
        actionButtons={[{
          id: 'btnUpdate',
          name: 'Save changes',
          iconName: 'Save',
          disabled: hasUnsavedChanges === false,
          onClick: onUpdateStormDoor,
        }, {
          id: 'btnViewAtVendor',
          name: 'View on vendor site',
          iconName: 'Globe',
          onClick: onViewOnVendorSite,
        }]}
      />
      <BwjPageContent contentDescription={'primary content (no subcontent)'}>
        <BwjGrid>
          <Row bottomSpacing={Spacing.Large}>
            <Col small={12} large={3}>
              <BwjTextField
                id={formService.brand.id}
                required={true}
                label={formService.brand.description}
                onUpdate={formService.brand.onUpdate}
                input={formModel.brand}
              />
            </Col>
            <Col small={12} large={9}>
              <BwjTextField
                id={formService.description.id}
                required={true}
                label={formService.description.description}
                onUpdate={formService.description.onUpdate}
                input={formModel.description}
              />
            </Col>
          </Row>
          <Row bottomSpacing={Spacing.Large}>
            <Col small={12} large={3}>
              <BwjSingleSelectField<IDoorSizeOption>
                id={formService.doorSize.id}
                required={true}
                label={formService.doorSize.description}
                onUpdate={formService.doorSize.onUpdate}
                input={formModel.doorSize}
                options={formService.doorSize.options}
                optionDisplayKey={'description'}
                optionValueKey={'id'}
              />
            </Col>
            <Col small={12} large={3}>
              <BwjSingleSelectField<IStormDoorHandleTypeOption>
                id={formService.validHandlesets.id}
                required={true}
                label={formService.validHandlesets.description}
                onUpdate={formService.validHandlesets.onUpdate}
                input={formModel.validHandlesets}
                options={formService.validHandlesets.options}
                optionDisplayKey={'description'}
                optionValueKey={'id'}
              />
            </Col>
            <Col small={12} large={3}>
              <BwjTextField
                id={formService.additionalInstallLabor.id}
                required={false}
                label={formService.additionalInstallLabor.description}
                onUpdate={formService.additionalInstallLabor.onUpdate}
                input={formModel.additionalInstallLabor}
              />
            </Col>
            <Col small={12} large={3}>
              <BwjTextField
                id={formService.laborFeeCoefficient.id}
                required={false}
                label={formService.laborFeeCoefficient.description}
                onUpdate={formService.laborFeeCoefficient.onUpdate}
                input={formModel.laborFeeCoefficient}
              />
            </Col>
          </Row>
          <Row bottomSpacing={Spacing.Large}>
            <Col small={12} large={4}>
              <BwjCheckbox
                id={formService.includesHandle.id}
                label={formService.includesHandle.description}
                checked={formModel.includesHandle}
                onChange={formService.includesHandle.onUpdate}
              />
            </Col>
            <Col small={12} large={4}>
              <BwjCheckbox
                id={formService.cutdownPossible.id}
                label={formService.cutdownPossible.description}
                checked={formModel.cutdownPossible}
                onChange={formService.cutdownPossible.onUpdate}
              />
            </Col>
            <Col small={12} large={4}>
              <BwjCheckbox
                id={formService.isNotAvailable.id}
                label={formService.isNotAvailable.description}
                checked={formModel.isNotAvailable}
                onChange={formService.isNotAvailable.onUpdate}
              />
            </Col>
          </Row>
          <Row bottomSpacing={Spacing.Large}>
            <Col small={12} large={4}>
              <BwjTextField
                id={formService.price.id}
                required={true}
                label={formService.price.description}
                onUpdate={formService.price.onUpdate}
                input={formModel.price}
                filter={/^[0-9.]*$/}
              />
            </Col>
            <Col small={12} large={4}>
              <BwjTextField
                id={formService.inStoreQuantity.id}
                required={true}
                label={formService.inStoreQuantity.description}
                onUpdate={formService.inStoreQuantity.onUpdate}
                input={formModel.inStoreQuantity}
                filter={/^[0-9]*$/}
              />
            </Col>
            <Col small={12} large={4}>
              <BwjTextField
                id={formService.pickupLeadTime.id}
                required={true}
                label={formService.pickupLeadTime.description}
                onUpdate={formService.pickupLeadTime.onUpdate}
                input={formModel.pickupLeadTime}
                filter={/^[0-9]*$/}
              />
            </Col>
          </Row>
          <Row bottomSpacing={Spacing.Large}>
            <Col small={12} large={6}>
              <BwjTextField
                id={formService.imageUrls.id}
                required={false}
                label={formService.imageUrls.description}
                onUpdate={formService.imageUrls.onUpdate}
                input={formModel.imageUrls}
                multiline={true}
              />
            </Col>
            <Col small={12} large={6}>
              <BwjImageBrowser
                id={`${formService.imageUrls.id}-image-viewer`}
                images={primaryImages.map(x => { return { src: x }; })}
                displayIndex={primaryImageDisplayIndex}
                loadImages={true}
                imageWidth={400}
                onDisplayIndexChanged={index => setPrimaryImageDisplayIndex(index)}
              />
            </Col>
          </Row>
          <Row bottomSpacing={Spacing.Large}>
            <Col small={12} large={6}>
              <BwjTextField
                id={formService.sellingPoints.id}
                required={false}
                label={formService.sellingPoints.description}
                onUpdate={formService.sellingPoints.onUpdate}
                input={formModel.sellingPoints}
                multiline={true}
              />
            </Col>
            <Col small={12} large={6}>
              <BwjTextField
                id={formService.productReviewSummary.id}
                required={false}
                label={formService.productReviewSummary.description}
                onUpdate={formService.productReviewSummary.onUpdate}
                input={formModel.productReviewSummary}
                multiline={true}
              />
            </Col>
          </Row>
          <Row bottomSpacing={Spacing.Large}>
            <Col small={12}>
              {formModel.textureVariants.map(variant =>
              <TextureVariantListItem
                key={variant.id}
                variant={variant}
                onClick={onVariantSelected}
                onGoToVendorSite={onViewVariantOnVendorSite}
              />)}
            </Col>
          </Row>
        </BwjGrid>
      </BwjPageContent>
    </BwjViewManager>
    </>
  );
};